import { Component, OnDestroy, OnInit, inject, signal, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { MatIconModule } from '@angular/material/icon';
import { KeycloakAuthService, UserProfile, isAdminUser, environment } from 'shared-services';
import { BrandHeroBackground } from './brand-hero-background/brand-hero-background';
import { TerminalHeroBackground } from './terminal-hero-background/terminal-hero-background';
import { SnakeGame } from './snake-game/snake-game';
import { WordleGame } from './wordle-game/wordle-game';
import { PongGame } from './pong-game/pong-game';
import { PacmanGame } from './pacman-game/pacman-game';
import { DoomGame } from './doom-game/doom-game';
import { BreakoutGame } from './breakout-game/breakout-game';
import { AsteroidsGame } from './asteroids-game/asteroids-game';
import { InvadersGame } from './invaders-game/invaders-game';
import { GalagaGame } from './galaga-game/galaga-game';
import { TetrisGame } from './tetris-game/tetris-game';

interface AppTile {
  title: string;
  description: string;
  icon: string;
  path: string;
  color: string;
  visible: boolean;
}

interface ExternalLink {
  title: string;
  description: string;
  icon: string;
  url: string;
  color: string;
}

interface EnvironmentOption {
  name: string;
  label: string;
  url: string;
  color: string;
}

@Component({
  selector: 'app-root',
  imports: [
    ToastModule,
    MatIconModule,
    BrandHeroBackground,
    TerminalHeroBackground,
    SnakeGame,
    WordleGame,
    PongGame,
    PacmanGame,
    DoomGame,
    BreakoutGame,
    AsteroidsGame,
    InvadersGame,
    GalagaGame,
    TetrisGame
  ],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private authService = inject(KeycloakAuthService);

  /**
   * Hidden "terminal mode" easter egg (1980s IBM CRT theme), unlocked by
   * typing "terminal" anywhere on the page. While terminal mode is active,
   * typing "snake", "pong", "pacman", "doom", "breakout", "asteroids",
   * "invaders", "galaga" or "tetris" unlocks the matching mini-game; typing "wordle"
   * while terminal mode is inactive unlocks the WORDLE mini-game instead.
   */
  terminalMode = signal(false);
  showSnakeGame = signal(false);
  showWordleGame = signal(false);
  showPongGame = signal(false);
  showPacmanGame = signal(false);
  showDoomGame = signal(false);
  showBreakoutGame = signal(false);
  showAsteroidsGame = signal(false);
  showInvadersGame = signal(false);
  showGalagaGame = signal(false);
  showTetrisGame = signal(false);
  private keyBuffer = '';
  private readonly terminalTrigger = 'terminal';
  private readonly snakeTrigger = 'snake';
  private readonly wordleTrigger = 'wordle';
  private readonly pongTrigger = 'pong';
  private readonly pacmanTrigger = 'pacman';
  private readonly doomTrigger = 'doom';
  private readonly breakoutTrigger = 'breakout';
  private readonly asteroidsTrigger = 'asteroids';
  private readonly invadersTrigger = 'invaders';
  private readonly galagaTrigger = 'galaga';
  private readonly tetrisTrigger = 'tetris';
  private readonly bufferLength = Math.max(
    this.terminalTrigger.length,
    this.snakeTrigger.length,
    this.wordleTrigger.length,
    this.pongTrigger.length,
    this.pacmanTrigger.length,
    this.doomTrigger.length,
    this.breakoutTrigger.length,
    this.asteroidsTrigger.length,
    this.invadersTrigger.length,
    this.galagaTrigger.length,
    this.tetrisTrigger.length
  );
  private readonly handleGlobalKeydown = (event: KeyboardEvent): void => {
    if (
      this.showSnakeGame() ||
      this.showWordleGame() ||
      this.showPongGame() ||
      this.showPacmanGame() ||
      this.showDoomGame() ||
      this.showBreakoutGame() ||
      this.showAsteroidsGame() ||
      this.showInvadersGame() ||
      this.showGalagaGame() ||
      this.showTetrisGame() ||
      event.key.length !== 1
    ) {
      return;
    }
    this.keyBuffer = (this.keyBuffer + event.key.toLowerCase()).slice(-this.bufferLength);
    if (this.keyBuffer.endsWith(this.terminalTrigger)) {
      this.toggleTerminalMode();
      this.keyBuffer = '';
    } else if (this.terminalMode() && this.keyBuffer.endsWith(this.snakeTrigger)) {
      this.showSnakeGame.set(true);
      this.keyBuffer = '';
    } else if (this.terminalMode() && this.keyBuffer.endsWith(this.pongTrigger)) {
      this.showPongGame.set(true);
      this.keyBuffer = '';
    } else if (this.terminalMode() && this.keyBuffer.endsWith(this.pacmanTrigger)) {
      this.showPacmanGame.set(true);
      this.keyBuffer = '';
    } else if (this.terminalMode() && this.keyBuffer.endsWith(this.doomTrigger)) {
      this.showDoomGame.set(true);
      this.keyBuffer = '';
    } else if (this.terminalMode() && this.keyBuffer.endsWith(this.breakoutTrigger)) {
      this.showBreakoutGame.set(true);
      this.keyBuffer = '';
    } else if (this.terminalMode() && this.keyBuffer.endsWith(this.asteroidsTrigger)) {
      this.showAsteroidsGame.set(true);
      this.keyBuffer = '';
    } else if (this.terminalMode() && this.keyBuffer.endsWith(this.invadersTrigger)) {
      this.showInvadersGame.set(true);
      this.keyBuffer = '';
    } else if (this.terminalMode() && this.keyBuffer.endsWith(this.galagaTrigger)) {
      this.showGalagaGame.set(true);
      this.keyBuffer = '';
    } else if (this.terminalMode() && this.keyBuffer.endsWith(this.tetrisTrigger)) {
      this.showTetrisGame.set(true);
      this.keyBuffer = '';
    } else if (!this.terminalMode() && this.keyBuffer.endsWith(this.wordleTrigger)) {
      this.showWordleGame.set(true);
      this.keyBuffer = '';
    }
  };

  loading = false;
  authInitialized = false;
  showNavigation = false;
  userProfile: UserProfile | null = null;
  tiles: AppTile[] = [];
  externalLinks: ExternalLink[] = [
    {
      title: 'Metrics',
      description: 'Monitor platform performance and dashboards.',
      icon: 'monitoring',
      url: 'https://grafana.monitoring.4cee-prd.net/dashboards/f/157c6e8e-2adb-41ee-8f96-b2dbd85d1a47',
      color: '#f59e0b'
    },
    {
      title: 'Documentation',
      description: 'Platform guides and technical documentation.',
      icon: 'menu_book',
      url: 'https://4cee.atlassian.net/wiki/spaces/DEV/pages/4264296449/Welcome+to+the+AC-IT+Documentation',
      color: '#2563eb'
    },
    {
      title: 'Git Repository',
      description: 'Source code and version control.',
      icon: 'code',
      url: 'https://gitlab.com/4cee/development/artificial-intelligence/ac-it',
      color: '#e24329'
    },
    {
      title: 'API Documentation',
      description: 'API reference and endpoint documentation.',
      icon: 'api',
      url: `${environment.apiBaseUrl}/docs`,
      color: '#10b981'
    }
  ];

  currentEnv = environment.envName;
  environments: EnvironmentOption[] = [
    { name: 'dev', label: 'Development', url: 'https://aidev.4cee-dev.net', color: '#22c55e' },
    { name: 'acc', label: 'Acceptance', url: 'https://ai.4cee-dev.net', color: '#f59e0b' },
    { name: 'prd', label: 'Production', url: 'https://ai.4cee.app', color: '#ef4444' }
  ];
  showEnvSwitcher = false;

  get currentEnvironment(): EnvironmentOption {
    return this.environments.find(e => e.name === this.currentEnv) ?? this.environments[0];
  }

  get displayName(): string {
    if (!this.userProfile) return '';
    if (this.userProfile.given_name && this.userProfile.family_name) {
      return `${this.userProfile.given_name} ${this.userProfile.family_name}`;
    }
    return this.userProfile.name || this.userProfile.email || '';
  }

  get greeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }

  constructor(private messageService: MessageService) {}

  async ngOnInit() {
    this.loading = true;

    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('keydown', this.handleGlobalKeydown);
      await this.initializeAuth();
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('keydown', this.handleGlobalKeydown);
      document.body.classList.remove('terminal-mode');
    }
  }

  private toggleTerminalMode(): void {
    const next = !this.terminalMode();
    this.terminalMode.set(next);
    if (next) {
      this.showWordleGame.set(false);
    } else {
      this.showSnakeGame.set(false);
      this.showPongGame.set(false);
      this.showPacmanGame.set(false);
      this.showDoomGame.set(false);
      this.showBreakoutGame.set(false);
      this.showAsteroidsGame.set(false);
      this.showInvadersGame.set(false);
      this.showGalagaGame.set(false);
      this.showTetrisGame.set(false);
    }
    document.body.classList.toggle('terminal-mode', next);
  }

  private async initializeAuth(): Promise<void> {
    try {
      const isAuthenticated = await this.authService.init();
      if (isAuthenticated) {
        this.userProfile = this.authService.identityClaims;
        this.authService.userProfile$.subscribe(profile => this.userProfile = profile);
        this.buildTiles();
      } else {
        this.authService.login();
      }
      this.showNavigation = true;
      this.authInitialized = true;
    } catch (err) {
      console.error('Auth initialization failed', err);
      this.authInitialized = true;
    } finally {
      this.loading = false;
    }
  }

  private buildTiles() {
    const permissions = this.getUserPermissions();

    this.tiles = [
      {
        title: 'Configurator',
        description: 'Configure and manage integration workflows and tenant settings.',
        icon: 'settings',
        path: '/configurator/',
        color: '#629afd',
        visible: this.hasPermissionForAnyTenant(permissions, ['*'])
      },
      {
        title: 'PDF2XML',
        description: 'Convert and process PDF documents to structured XML.',
        icon: 'picture_as_pdf',
        path: '/pdf2xml/',
        color: '#ee506f',
        visible: this.hasTenantPermissions(permissions, ['flow:execute', 'feedback:write', 'flow:read_execution_log', 'storage:create_presigned_url'])
      },
      {
        title: 'Chat',
        description: 'AI-powered chat interface for automated workflows.',
        icon: 'chat',
        path: '/chat/',
        color: '#f38363',
        visible: this.hasSpecificTenantPermissions(permissions, 'chat', ['flow:execute', 'feedback:write', 'flow:read_execution_log', 'storage:create_presigned_url'])
      },
      {
        title: 'XSLT Builder',
        description: 'Build, inspect, and apply XSLT mappings and transformations.',
        icon: 'construction',
        path: '/xslt-builder/',
        color: '#0ea5e9',
        visible: this.hasSpecificTenantPermissions(permissions, 'internal', ['flow:execute', 'flow:read_execution_log'])
      },
      {
        title: 'Prototypes',
        description: 'Open and try out prototypes for validation.',
        icon: 'science',
        path: '/prototype/',
        color: '#14b8a6',
        visible: this.hasSpecificTenantPermissions(permissions, 'prototype', ['flow:execute'])
      },
      {
        title: 'Admin Portal',
        description: 'Manage users, roles, and platform administration.',
        icon: 'admin_panel_settings',
        path: '/admin/',
        color: '#8770ff',
        visible: isAdminUser()
      }
    ].filter(tile => tile.visible);
  }

  private getUserPermissions(): Record<string, string[]> {
    try {
      const raw = localStorage.getItem('userPermissions');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  private hasPermissionForAnyTenant(permissions: Record<string, string[]>, required: string[]): boolean {
    const wildcardPerms = permissions['*'] ?? [];
    if (wildcardPerms.includes('*')) return true;

    for (const tenant of Object.keys(permissions)) {
      const tenantPerms = [...(permissions[tenant] ?? []), ...wildcardPerms];
      if (tenantPerms.includes('*') || required.every(p => tenantPerms.includes(p))) {
        return true;
      }
    }
    return false;
  }

  private hasTenantPermissions(permissions: Record<string, string[]>, required: string[]): boolean {
    const wildcardPerms = permissions['*'] ?? [];
    if (wildcardPerms.includes('*')) return true;

    for (const [tenant, tenantPerms] of Object.entries(permissions)) {
      if (tenant === '*') continue;
      const effective = [...tenantPerms, ...wildcardPerms];
      if (effective.includes('*') || required.every(p => effective.includes(p))) {
        return true;
      }
    }
    return false;
  }

  private hasSpecificTenantPermissions(permissions: Record<string, string[]>, tenantId: string, required: string[]): boolean {
    const wildcardPerms = permissions['*'] ?? [];
    if (wildcardPerms.includes('*')) return true;

    const tenantPerms = permissions[tenantId] ?? [];
    const effective = [...tenantPerms, ...wildcardPerms];
    if (effective.includes('*')) return true;

    return required.every(p => effective.includes(p));
  }

  navigateHome() {
    // Relative to <base href>, so it also works under the MR preview prefix (/mr-<iid>/)
    window.location.href = new URL('./', document.baseURI).href;
  }

  openTile(tile: AppTile) {
    // Resolve against <base href> instead of the host root, so MR previews stay under /mr-<iid>/
    window.location.href = new URL(tile.path.replace(/^\/+/, ''), document.baseURI).href;
  }

  openExternalLink(link: ExternalLink, event: MouseEvent) {
    if (!link.url) {
      event.preventDefault();
    }
  }

  logout() {
    this.authService.logout();
  }

  toggleEnvSwitcher() {
    this.showEnvSwitcher = !this.showEnvSwitcher;
  }

  switchEnvironment(env: EnvironmentOption) {
    if (env.name !== this.currentEnv) {
      window.location.href = env.url;
    }
    this.showEnvSwitcher = false;
  }

  triggerLogin() {
    this.authService.login();
  }

  exitTerminalMode() {
    if (this.terminalMode()) {
      this.toggleTerminalMode();
    }
  }
}
